import { useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';

export default function IntegrationsPage() {
  const [integrations] = useState([
    {
      id: 'slack',
      name: 'Slack',
      description: 'Send notifications and updates to your Slack workspace',
      icon: '💬',
      category: 'communication',
      connected: true,
      config: {
        webhook: 'https://hooks.slack.com/services/...',
        channel: '#general',
      },
    },
    {
      id: 'github',
      name: 'GitHub',
      description: 'Sync issues and track time on repositories',
      icon: '📦',
      category: 'development',
      connected: false,
      config: {},
    },
    {
      id: 'google-calendar',
      name: 'Google Calendar',
      description: 'Sync meetings and schedule time blocks',
      icon: '📅',
      category: 'productivity',
      connected: true,
      config: {
        calendarId: 'primary',
        syncMeetings: true,
      },
    },
    {
      id: 'trello',
      name: 'Trello',
      description: 'Import boards and sync task progress',
      icon: '📋',
      category: 'productivity',
      connected: false,
      config: {},
    },
    {
      id: 'zapier',
      name: 'Zapier',
      description: 'Connect with 5000+ apps through automation',
      icon: '⚡',
      category: 'automation',
      connected: false,
      config: {},
    },
    {
      id: 'quickbooks',
      name: 'QuickBooks',
      description: 'Sync invoices and financial data',
      icon: '💰',
      category: 'finance',
      connected: false,
      config: {},
    },
  ]);

  const [apiKeys] = useState([
    {
      id: '1',
      name: 'Production API',
      key: 'flk_live_1234567890abcdef',
      created: '2025-01-15T10:00:00Z',
      lastUsed: '2025-09-28T14:30:00Z',
      permissions: ['read', 'write'],
    },
    {
      id: '2',
      name: 'Development API',
      key: 'flk_test_abcdef1234567890',
      created: '2025-02-01T09:00:00Z',
      lastUsed: '2025-09-27T16:45:00Z',
      permissions: ['read'],
    },
  ]);

  const [webhooks] = useState([
    {
      id: '1',
      name: 'Task Updates',
      url: 'https://api.example.com/webhooks/tasks',
      events: ['task.created', 'task.updated', 'task.completed'],
      active: true,
    },
    {
      id: '2',
      name: 'Time Tracking',
      url: 'https://hooks.example.com/time',
      events: ['time.started', 'time.stopped'],
      active: false,
    },
  ]);

  const categories = [
    { id: 'all', name: 'All', count: integrations.length },
    {
      id: 'communication',
      name: 'Communication',
      count: integrations.filter((i) => i.category === 'communication').length,
    },
    {
      id: 'development',
      name: 'Development',
      count: integrations.filter((i) => i.category === 'development').length,
    },
    {
      id: 'productivity',
      name: 'Productivity',
      count: integrations.filter((i) => i.category === 'productivity').length,
    },
    {
      id: 'automation',
      name: 'Automation',
      count: integrations.filter((i) => i.category === 'automation').length,
    },
    {
      id: 'finance',
      name: 'Finance',
      count: integrations.filter((i) => i.category === 'finance').length,
    },
  ];

  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredIntegrations =
    selectedCategory === 'all'
      ? integrations
      : integrations.filter((i) => i.category === selectedCategory);

  const handleConnect = (integrationId: string) => {
    console.log('Connecting integration:', integrationId);
  };

  const handleDisconnect = (integrationId: string) => {
    console.log('Disconnecting integration:', integrationId);
  };

  const handleCreateApiKey = () => {
    console.log('Creating new API key');
  };

  const handleRevokeApiKey = (keyId: string) => {
    console.log('Revoking API key:', keyId);
  };

  const handleCreateWebhook = () => {
    console.log('Creating new webhook');
  };

  const handleDeleteWebhook = (webhookId: string) => {
    console.log('Deleting webhook:', webhookId);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <AppLayout>
      <Breadcrumb items={[{ label: 'Integrations' }]} />

      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Integrations</h1>
          <p className="text-gray-600">Connect with third-party tools and services</p>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                selectedCategory === category.id
                  ? 'bg-flowky-primary text-white border-flowky-primary'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </div>

        {/* Available Integrations */}
        <div className="border border-border rounded-lg p-4 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Integrations</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredIntegrations.map((integration) => (
              <div
                key={integration.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-white/30 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{integration.icon}</div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{integration.name}</h4>
                      <div className="text-xs text-gray-500 capitalize">{integration.category}</div>
                    </div>
                  </div>
                  <div
                    className={`px-2 py-1 text-xs rounded-full ${
                      integration.connected
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {integration.connected ? 'Connected' : 'Available'}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">{integration.description}</p>
                <div className="flex gap-2">
                  {integration.connected ? (
                    <>
                      <button className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded hover:bg-white/30 transition-colors">
                        Configure
                      </button>
                      <button
                        onClick={() => handleDisconnect(integration.id)}
                        className="px-3 py-2 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50 transition-colors"
                      >
                        Disconnect
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleConnect(integration.id)}
                      className="w-full px-3 py-2 text-sm bg-flowky-primary text-white rounded hover:bg-flowky-primary/90 transition-colors"
                    >
                      Connect
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* API Keys */}
        <div className="border border-border rounded-lg p-4 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">API Keys</h3>
            <button
              onClick={handleCreateApiKey}
              className="px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors"
            >
              Create API Key
            </button>
          </div>
          <div className="space-y-3">
            {apiKeys.map((key) => (
              <div
                key={key.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div>
                  <div className="font-medium text-gray-900">{key.name}</div>
                  <div className="text-sm text-gray-600 font-mono">{key.key}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Created: {formatDate(key.created)} • Last used: {formatDate(key.lastUsed)}
                  </div>
                  <div className="flex gap-1 mt-2">
                    {key.permissions.map((permission) => (
                      <span
                        key={permission}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded"
                      >
                        {permission}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-white/30 transition-colors">
                    Edit
                  </button>
                  <button
                    onClick={() => handleRevokeApiKey(key.id)}
                    className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50 transition-colors"
                  >
                    Revoke
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Webhooks */}
        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Webhooks</h3>
            <button
              onClick={handleCreateWebhook}
              className="px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors"
            >
              Create Webhook
            </button>
          </div>
          <div className="space-y-3">
            {webhooks.map((webhook) => (
              <div
                key={webhook.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-gray-900">{webhook.name}</div>
                    <div
                      className={`px-2 py-1 text-xs rounded-full ${
                        webhook.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {webhook.active ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 font-mono">{webhook.url}</div>
                  <div className="flex gap-1 mt-2">
                    {webhook.events.map((event) => (
                      <span
                        key={event}
                        className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded"
                      >
                        {event}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-white/30 transition-colors">
                    Edit
                  </button>
                  <button className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-white/30 transition-colors">
                    Test
                  </button>
                  <button
                    onClick={() => handleDeleteWebhook(webhook.id)}
                    className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Developer Resources */}
        <div className="border border-border rounded-lg p-4 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Developer Resources</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="#"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-white/30 transition-colors"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-flowky-secondary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <div className="font-medium text-gray-900">API Documentation</div>
                <div className="text-sm text-gray-600">Complete API reference</div>
              </div>
            </a>
            <a
              href="#"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-white/30 transition-colors"
            >
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                  />
                </svg>
              </div>
              <div>
                <div className="font-medium text-gray-900">Code Examples</div>
                <div className="text-sm text-gray-600">Integration code samples</div>
              </div>
            </a>
            <a
              href="#"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-white/30 transition-colors"
            >
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <div className="font-medium text-gray-900">Support</div>
                <div className="text-sm text-gray-600">Get help with integrations</div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
